import React, { useContext, useMemo } from 'react';
import { LeedflowLogoLight as logoLeedflow } from '@leedpulse-monorepo/assets';
import { LeedflowLogoMinimalist as logoLeedflowCollapse } from '@leedpulse-monorepo/assets';
import { message } from 'antd';
import { CustomerServiceOutlined } from '@ant-design/icons';
import {
  DashboardMenu as LeedDashboardMenu,
  DashboardMenuItem,
} from '@leedpulse-monorepo/components';
import { AccountSelector } from '../../../components/account-selector';
import DashboardMenuUserProfile from './user_profile/DashboardMenuUserProfile';
import { useLocation, useNavigate } from 'react-router-dom';
import { SETTINGS_PAGE_PATH } from '../../../pages/settings/SettingsPage';
import { OVERVIEW_PAGE_PATH } from '../../../pages/OverviewPage';
import { CONTACTS_PAGE_PATH } from '../../../pages/contacts/ContactsPage';
import { CONVERSATIONS_PAGE_PATH } from '../../../pages/conversations/ConversationsPage';
import { navigationConfig } from '../../../AppRouter';
import AppContext from '../../../contexts/data/AppContext';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { UserRole } from '@leedpulse-monorepo/enums';
import { COMPANY_PAGE_PATH } from '../../../pages/company/CompanyPage';
import { useCompanyTabs } from '../../../pages/company/useCompanyTabs';

import styles from './DashboardMenu.module.css';

interface DashboardMenuProps {
  show: boolean;
  isCollapsed: boolean;
  onNavigate: () => void;
  onCollapseChange: (isCollapsed: boolean) => void;
}

const DashboardMenu: React.FC<DashboardMenuProps> = ({
  show,
  isCollapsed,
  onNavigate,
  onCollapseChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // a detail sub-route (e.g. /company/ai-receptionist/:subscriptionId) should keep its parent nav item highlighted
  const selectedKey =
    navigationConfig.find(
      (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
    )?.path ?? location.pathname;

  const appContext = useContext(AppContext);
  const { userDoc } = useContext(AuthenticationContext);
  const { companies, activeCompany, setActiveCompany } = useContext(CompanyContext);
  const companyTabs = useCompanyTabs();

  const buildVersion = () => {
    const sBuildDate = appContext.item?.buildDate;
    if (sBuildDate) {
      const sDate = sBuildDate.split('T')[0];
      const sTime = sBuildDate.split('T')[1];
      const sYear = sDate.split('-')[0].slice(2, 4);
      const sMonth = sDate.split('-')[1];
      const sDay = sDate.split('-')[2];
      const sHours = sTime.split(':')[0];
      const sMinutes = sTime.split(':')[1];
      return `${sYear}${sMonth}${sDay}.${sHours}.${sMinutes}`;
    }
  };

  const handleAccountSelectorChange = (accountId: string) => {
    if (accountId === 'new') {
      message.info(`L'ajout d'une nouvelle entreprise est en cours de développement`);
    } else {
      setActiveCompany(accountId);
    }
  };

  const isItemHidden = (path: string): boolean => {
    const allowedSimpleUserPaths = [OVERVIEW_PAGE_PATH, CONTACTS_PAGE_PATH, CONVERSATIONS_PAGE_PATH];

    if (!activeCompany?.isSupervisor) {
      // a member only reaches "Mon entreprise" through their own receptionist
      if (path === COMPANY_PAGE_PATH) return companyTabs.length === 0;
      return !allowedSimpleUserPaths.includes(path);
    }
    return false;
  };

  const items: DashboardMenuItem[] = useMemo(
    () =>
      navigationConfig.filter((item) => !isItemHidden(item.path)).map((item) => ({
        key: item.path,
        title: item.title,
        icon: item.icon,
        iconActive: item.iconActive,
        dividerBefore: item.dividerBefore,
        onClick: () => navigate(item.path),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeCompany, companyTabs.length]
  );

  const footerItems: DashboardMenuItem[] = [
    {
      key: 'contact',
      icon: <CustomerServiceOutlined />,
      title: 'Nous contacter',
      primary: true,
      onClick: () => {
        const subject = `Demande de support - ${activeCompany?.name ?? ''}`;
        const body = [
          'Bonjour,',
          '',
          '',
          '',
          '---',
          'Informations du compte :',
          `Nom : ${userDoc?.firstname ?? ''} ${userDoc?.lastname ?? ''}`,
          `Email du compte : ${userDoc?.email ?? ''}`,
          `Entreprise : ${activeCompany?.name ?? ''}`,
        ].join('\r\n');
        window.location.href = `mailto:help@leedflow.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        // no way to know if a mail app opened, so the address is always shown
        message.info(`Si votre messagerie ne s'ouvre pas, écrivez-nous à help@leedflow.com`, 8);
      },
    },
  ];

  return (
    <LeedDashboardMenu
      items={items}
      selectedKey={selectedKey}
      show={show}
      isCollapsed={isCollapsed}
      onCollapseChange={onCollapseChange}
      onNavigate={onNavigate}
      logo={logoLeedflow}
      logoCollapsed={logoLeedflowCollapse}
      onLogoClick={() => navigate('/')}
      version={buildVersion()}
      menuTheme={{
        itemHeight: 48,
        itemBorderRadius: 12,
        itemMarginBlock: 4,
        itemPaddingInline: 16,
        itemSelectedBg: 'rgba(52, 199, 89, 0.12)',
        itemSelectedColor: '#34C759',
        itemHoverBg: 'rgba(52, 199, 89, 0.08)',
        itemHoverColor: '#34C759',
        iconSize: 18,
        fontSize: 15,
      }}
      beforeNavigation={
        <AccountSelector
          companies={companies}
          selectedAccountId={activeCompany?.id ?? ''}
          onChange={handleAccountSelectorChange}
          className={styles.dashboardMenuSubscriptionSelect}
          showExtra={[UserRole.owner].includes(userDoc?.role ?? UserRole.customer)}
        />
      }
      footerItems={footerItems}
      footer={(isCollapsedView) => (
        <DashboardMenuUserProfile
          isCollapsed={isCollapsedView}
          userName={userDoc ? `${userDoc.firstname} ${userDoc.lastname}` : ''}
          userEmail={userDoc?.email ?? ''}
          avatarUrl={userDoc?.avatar}
          onClick={() => {
            navigate(SETTINGS_PAGE_PATH);
            onNavigate();
            onCollapseChange(true);
          }}
        />
      )}
    />
  );
};

export default DashboardMenu;
